import React, { useEffect, useState } from "react";
import { Badge, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getIncidentTypeImagePath } from "../../../shared/functions/incidentDisplay";
import BreadcrumbItem from "../../../shared/components/BreadcrumbItem";
import { WebStatisticsService } from "./service";
import { IStatistics } from "../../../shared/interfaces/statistics/IStatistics";
import { handleError } from "../../../shared/functions/handleError";

/* ─── helpers ────────────────────────────────────────────────────────────── */

const getIncidentTypeColor = (type: string): string => {
  switch (type) {
    case "ACTIVE_SHOOTER":
    case "KNIFE_ATTACK":
    case "VIOLENT_ACTIVITY":
    case "SECURITY_THREAT":
      return "#dc3545";
    case "FIRE_EMERGENCY":
    case "FIRE":
      return "#fd7e14";
    case "MEDICAL":
      return "#0dcaf0";
    case "NATURAL_DISASTER":
    case "HAZMAT":
      return "#6c757d";
    default:
      return "#6366f1";
  }
};

// Statistics cards always need a picture, so unknown types fall back to this one
const DEFAULT_INCIDENT_IMAGE = "/assets/images/active_shooter.png";

const getIncidentImage = (type: string): string =>
  getIncidentTypeImagePath(type) ?? DEFAULT_INCIDENT_IMAGE;

const formatDate = (dateString?: string) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString();
};

/* ─── sub-components ─────────────────────────────────────────────────────── */

interface IStatCardProps {
  title: string;
  value: number | string;
  icon: string;
  color: string;
  subtitle?: string;
}

const StatCard = ({ title, value, icon, color, subtitle }: IStatCardProps) => (
  <Card
    className="h-100"
    style={{
      borderTop: `3px solid ${color}`,
      borderRadius: 12,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    }}
  >
    <Card.Body className="d-flex align-items-center gap-3">
      <div
        className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
        style={{
          width: 56,
          height: 56,
          backgroundColor: `${color}18`,
        }}
      >
        <i className={icon} style={{ fontSize: 28, color }} />
      </div>
      <div>
        <div className="text-muted" style={{ fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {title}
        </div>
        <div className="fw-bold" style={{ fontSize: 32, color, lineHeight: 1.1 }}>
          {value}
        </div>
        {subtitle && (
          <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
            {subtitle}
          </div>
        )}
      </div>
    </Card.Body>
  </Card>
);

/* ─── main page ──────────────────────────────────────────────────────────── */

const OperatorStatistics = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<IStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    WebStatisticsService.get()
      .then((response) => setStats(response.data))
      .catch(handleError)
      .finally(() => setIsLoading(false));
  }, []);

  const totalIncidents = stats
    ? Object.values(stats.totalIncidentsByType).reduce((a, b) => a + b, 0)
    : 0;

  const maxTypeCount = stats
    ? Math.max(...Object.values(stats.totalIncidentsByType), 1)
    : 1;

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("statistics.title")}
        subTitle={t("statistics.subtitle")}
      />
      <div className="mb-4">
        <Badge bg="primary" className="px-3 py-2 rounded-pill">
          <i className="ph-bold ph-calendar-blank me-1" />
          Past 30 Days
        </Badge>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : !stats ? (
        <Card className="text-center py-5">
          <Card.Body>
            <i className="ph-duotone ph-chart-bar" style={{ fontSize: 64, opacity: 0.3 }} />
            <h5 className="mt-3 text-muted">{t("statistics.noData")}</h5>
          </Card.Body>
        </Card>
      ) : (
        <>
          {/* ── Top KPI cards ── */}
          <Row className="g-3 mb-4">
            <Col xl={3} md={6}>
              <StatCard
                title={t("statistics.totalIncidents")}
                value={totalIncidents}
                icon="ph-duotone ph-warning-octagon"
                color="#6366f1"
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                title={t("statistics.totalResolved")}
                value={stats.totalResolvedIncidents}
                icon="ph-duotone ph-check-circle"
                color="#22c55e"
                subtitle={
                  totalIncidents > 0
                    ? `${Math.round((stats.totalResolvedIncidents / totalIncidents) * 100)}% resolution rate`
                    : undefined
                }
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                title={t("statistics.totalInstitutions")}
                value={stats.institutions.length}
                icon="ph-duotone ph-buildings"
                color="#0ea5e9"
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                title={t("statistics.totalUsers")}
                value={stats.users.length}
                icon="ph-duotone ph-users"
                color="#f59e0b"
              />
            </Col>
          </Row>

          {/* ── Middle row ── */}
          <Row className="g-3 mb-4">
            {/* Incidents by Type */}
            <Col xl={6}>
              <Card
                className="h-100"
                style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
              >
                <Card.Header className="bg-transparent border-bottom-0 pt-4 pb-0 px-4">
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <span>
                      <i className="ph-duotone ph-chart-bar me-2 text-primary" />
                      {t("statistics.incidentsByType")}
                    </span>
                    <Badge bg="light" text="dark" className="fw-normal" style={{ fontSize: 11 }}>30 Days</Badge>
                  </h5>
                  <small className="text-muted">{t("statistics.incidentsByTypeDesc")}</small>
                </Card.Header>
                <Card.Body className="px-4">
                  {Object.keys(stats.totalIncidentsByType).length === 0 ? (
                    <p className="text-muted">{t("statistics.noData")}</p>
                  ) : (
                    <div className="d-flex flex-column gap-3 mt-2">
                      {Object.entries(stats.totalIncidentsByType)
                        .sort(([, a], [, b]) => b - a)
                        .map(([type, count]) => {
                          const color = getIncidentTypeColor(type);
                          const pct = Math.round((count / maxTypeCount) * 100);
                          return (
                            <div key={type}>
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-2 d-flex align-items-center justify-content-center"
                                    style={{
                                      width: 32,
                                      height: 32,
                                      backgroundColor: `${color}18`,
                                      flexShrink: 0,
                                    }}
                                  >
                                      <div
                                        style={{
                                          width: 18,
                                          height: 18,
                                          backgroundColor: color,
                                          WebkitMaskImage: `url(${getIncidentImage(type)})`,
                                          WebkitMaskSize: "contain",
                                          WebkitMaskRepeat: "no-repeat",
                                          WebkitMaskPosition: "center",
                                          maskImage: `url(${getIncidentImage(type)})`,
                                          maskSize: "contain",
                                          maskRepeat: "no-repeat",
                                          maskPosition: "center",
                                        }}
                                      />
                                  </div>
                                  <span className="fw-medium" style={{ fontSize: 14 }}>
                                    {t(`incidentType.${type}`, { defaultValue: type })}
                                  </span>
                                </div>
                                <span
                                  className="fw-bold"
                                  style={{ fontSize: 18, color }}
                                >
                                  {count}
                                </span>
                              </div>
                              <div
                                className="rounded-pill overflow-hidden"
                                style={{ height: 8, backgroundColor: `${color}18` }}
                              >
                                <div
                                  className="rounded-pill"
                                  style={{
                                    height: "100%",
                                    width: `${pct}%`,
                                    backgroundColor: color,
                                    transition: "width 0.6s ease",
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Institutions */}
            <Col xl={6}>
              <Card
                className="h-100"
                style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
              >
                <Card.Header className="bg-transparent border-bottom-0 pt-4 pb-0 px-4">
                  <h5 className="mb-0">
                    <i className="ph-duotone ph-buildings me-2 text-info" />
                    {t("statistics.institutionsList")}
                  </h5>
                  <small className="text-muted">{t("statistics.institutionsListDesc")}</small>
                </Card.Header>
                <Card.Body className="px-4">
                  {stats.institutions.length === 0 ? (
                    <p className="text-muted">{t("statistics.noData")}</p>
                  ) : (
                    <div className="d-flex flex-column gap-2 mt-2">
                      {stats.institutions.map((inst) => (
                        <div
                          key={inst.id}
                          className="d-flex align-items-center gap-3 p-3 rounded-3"
                          style={{ backgroundColor: "rgba(0,0,0,0.03)", border: "1px solid rgba(0,0,0,0.06)" }}
                        >
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-info flex-shrink-0"
                            style={{
                              width: 40,
                              height: 40,
                              backgroundColor: "#0ea5e918",
                              fontSize: 14,
                            }}
                          >
                            {inst.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 14 }}>
                              {inst.name}
                            </div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              ID: {inst.id}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Recent Activity Table ── */}
          <Row className="g-3">
            <Col>
              <Card style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                <Card.Header className="bg-transparent border-bottom pt-4 pb-3 px-4">
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <span>
                      <i className="ph-duotone ph-clock-countdown me-2 text-warning" />
                      {t("statistics.recentActivity")}
                    </span>
                    <Badge bg="light" text="dark" className="fw-normal" style={{ fontSize: 11 }}>30 Days</Badge>
                  </h5>
                  <small className="text-muted">{t("statistics.recentActivityDesc")}</small>
                </Card.Header>
                <Card.Body className="p-0">
                  {stats.users.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      {t("statistics.noData")}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="mb-0" hover>
                        <thead style={{ backgroundColor: "rgba(0,0,0,0.02)" }}>
                          <tr>
                            <th className="px-4 py-3 border-0 fw-semibold text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              User
                            </th>
                            <th className="py-3 border-0 fw-semibold text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {t("incident.incidentType")}
                            </th>
                            <th className="py-3 border-0 fw-semibold text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {t("incident.status")}
                            </th>
                            <th className="py-3 border-0 fw-semibold text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {t("incident.drillMode")}
                            </th>
                            <th className="py-3 border-0 fw-semibold text-muted" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {t("incident.createdAt")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.users.map((user, idx) => {
                            const color = getIncidentTypeColor(user.incidentType);
                            return (
                              <tr key={idx}>
                                <td className="px-4 py-3 align-middle">
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                      style={{
                                        width: 36,
                                        height: 36,
                                        backgroundColor: "#6366f118",
                                        color: "#6366f1",
                                        fontSize: 13,
                                      }}
                                    >
                                      {user.firstName?.[0]}{user.lastName?.[0]}
                                    </div>
                                    <div>
                                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                                        {user.firstName} {user.lastName}
                                      </div>
                                      <div className="text-muted" style={{ fontSize: 12 }}>
                                        {user.email}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 align-middle">
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="rounded-2 d-flex align-items-center justify-content-center"
                                      style={{
                                        width: 28,
                                        height: 28,
                                        backgroundColor: `${color}18`,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: 16,
                                          height: 16,
                                          backgroundColor: color,
                                          WebkitMaskImage: `url(${getIncidentImage(user.incidentType)})`,
                                          WebkitMaskSize: "contain",
                                          WebkitMaskRepeat: "no-repeat",
                                          WebkitMaskPosition: "center",
                                          maskImage: `url(${getIncidentImage(user.incidentType)})`,
                                          maskSize: "contain",
                                          maskRepeat: "no-repeat",
                                          maskPosition: "center",
                                        }}
                                      />
                                    </div>
                                    <span style={{ fontSize: 13 }}>
                                      {t(`incidentType.${user.incidentType}`, { defaultValue: user.incidentType })}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-3 align-middle">
                                  <Badge
                                    bg={
                                      user.statusType === "RESOLVED"
                                        ? "success"
                                        : user.statusType === "IN_PROGRESS"
                                          ? "info"
                                          : user.statusType === "PENDING"
                                            ? "warning"
                                            : "secondary"
                                    }
                                    style={{ fontSize: 11 }}
                                  >
                                    {t(`incident.statusType.${user.statusType}`, { defaultValue: user.statusType })}
                                  </Badge>
                                </td>
                                <td className="py-3 align-middle">
                                  <Badge
                                    bg={user.drillMode ? "warning" : "secondary"}
                                    style={{ fontSize: 11 }}
                                  >
                                    {user.drillMode ? "True" : "False"}
                                  </Badge>
                                </td>
                                <td className="py-3 align-middle text-muted" style={{ fontSize: 13 }}>
                                  <i className="ti ti-clock me-1" />
                                  {formatDate(user.createdAt)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </React.Fragment>
  );
};

export default OperatorStatistics;
